import { GridPreProcessEditCellProps } from '@mui/x-data-grid-pro';

import { VALIDATION_REGEX } from 'common/src/validators/validationRegex';

import InRowCellValidator from './InRowCellValidator';

import {
  asDate,
  getDateBoundsError,
  validateEndDate,
} from '../../../validators/dateValidator';
import { VALIDATION_MSG } from '../forms/validationMessages';

interface DateBoundsOptions {
  minDate?: Date;
  maxDate?: Date;
  minDateError?: string;
  maxDateError?: string;
}

const isEmptyValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  return false;
};

export const preProcessRequiredField = (
  params: GridPreProcessEditCellProps,
) => {
  const error = isEmptyValue(params.props.value)
    ? VALIDATION_MSG.required
    : null;

  return { ...params.props, error };
};

export const preProcessEmailField = (params: GridPreProcessEditCellProps) => {
  const value = params.props.value;

  if (isEmptyValue(value)) {
    return { ...params.props, error: VALIDATION_MSG.required };
  }

  const email = String(value).trim();
  const error = VALIDATION_REGEX.email.test(email)
    ? null
    : VALIDATION_MSG.invalidEmail;

  return { ...params.props, error };
};

export const preProcessPhoneField = (params: GridPreProcessEditCellProps) => {
  const value = params.props.value;

  if (isEmptyValue(value)) {
    return { ...params.props, error: null };
  }

  const phone = String(value).trim();
  const error = VALIDATION_REGEX.usaPhone.test(phone)
    ? null
    : VALIDATION_MSG.invalidPhoneNumber;

  return { ...params.props, error };
};

export const preProcessStartEndDates = (
  params: GridPreProcessEditCellProps,
  editedField: 'startDate' | 'endDate',
  options?: DateBoundsOptions,
) => {
  const { props, row } = params;
  const otherFieldsProps = (params as any).otherFieldsProps;

  const boundsError = getDateBoundsError(props.value, options);

  if (boundsError) {
    return { ...props, error: boundsError };
  }

  const getFieldValue = (field: 'startDate' | 'endDate') =>
    field === editedField
      ? props.value
      : (otherFieldsProps?.[field]?.value ?? row?.[field] ?? null);

  const startDate = asDate(getFieldValue('startDate'));
  const endDate = asDate(getFieldValue('endDate'));

  const hasInvalidRange =
    !!startDate && !!endDate && !validateEndDate(endDate, startDate);

  if (!hasInvalidRange) {
    return { ...props, error: null };
  }

  return {
    ...props,
    error:
      editedField === 'startDate'
        ? VALIDATION_MSG.invalidStartDate
        : VALIDATION_MSG.invalidEndDate,
  };
};

export const getStartAndEndDateInRowValidation = (
  options?: DateBoundsOptions,
) => ({
  startDate: {
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
      preProcessStartEndDates(params, 'startDate', options),
    renderEditCell: (params) => (
      <InRowCellValidator
        {...params}
        maxDate={options?.maxDate}
        minDate={options?.minDate}
        placement="bottom-end"
      />
    ),
  },
  endDate: {
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
      preProcessStartEndDates(params, 'endDate', options),
    renderEditCell: (params) => (
      <InRowCellValidator
        {...params}
        maxDate={options?.maxDate}
        minDate={options?.minDate}
        placement="bottom-start"
      />
    ),
  },
});
